import { hasOnlyIncompleteTariffs } from '@billing/validation/incompleteClaim';
// React
import React, {
  MutableRefObject,
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

// Refine
import { useTranslation } from '@refinedev/core';
import {
  useClaimComments,
  useCreateClaimComment,
} from 'api/resource/claimComment';

// Mantine
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Collapse,
  Flex,
  Group,
  Indicator,
  LoadingOverlay,
  OptionalPortal,
  Tabs,
  Text,
  UnstyledButton,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { FormErrors } from '@mantine/form';
import { useMediaQuery } from '@mantine/hooks';
import { useIsMutating } from '@tanstack/react-query';
import { MRT_Row, MRT_TableInstance } from 'mantine-react-table';

//Icons
import {
  IconCircleCheck,
  IconChevronDown,
  IconDeviceFloppy,
  IconHistory,
  IconX,
} from '@tabler/icons-react';

//Interfaces
import { Claim, Payor, Provider } from 'interfaces';

import {
  CLAIM_TARIFF_STATUS,
  ClaimConfidenceInfo,
  UNRECONCILED_CLAIM_TARIFF_STATUSES,
  claimEditSaveSchema,
} from '@billing/validation';

//Components
import {
  AllKeys,
  ClaimDetailSection,
  ClaimEOBSection,
  ClaimHealthcardSection,
  ClaimInfoSection,
  ClaimCommentSection,
  ClaimModalSubmitButton,
  ClaimPatientSection,
  ClaimProviderSection,
  ClaimTariffSection,
  editDetailSectionKeys,
  claimCommentSectionKeys,
  editInfoSectionKeys,
  SKClaimDetailSectionKeys,
  eobSectionKeys as eobKeys,
  healthcardSectionKeys as healthcardKeys,
  PatientHistoryModal,
  RelatedClaimsPanel,
  patientSectionKeys as patientKeys,
  providerSectionKeys as providerKeys,
  tariffSectionKeys as tariffKeys,
  useActiveForm,
} from 'pages/claims/components';

// Utils
import { isSubmittedClaimPayorEditable } from 'utils/actionsUtils';
import { buttonTextColor } from 'utils/buttonUtils';
import { claimHasAnyTariffStatus } from 'utils/claimStatusUtils';
import { getButtonShakeAnimation } from 'utils/styleUtils';

import * as Sentry from '@sentry/react';
import { useClaimDraftStore } from 'store/ClaimDraftStore';
import { useSettingsStore } from 'store/SettingsStore';
import { useCurrentSiteStore } from 'store/SiteStore';

import { useCanModify } from 'hooks/useCanModify';
import { getCognitoRole } from 'api/utils/auth';
import { useIsSKClaim } from 'hooks/useIsSKClaim';
import { isFutureDate } from 'utils/dateParsing';
import { FutureServiceDateAlert } from './FutureServiceDateAlert';
import type { LiveClaimRulesState } from 'hooks/useLiveClaimRules';
import { useApplyLiveRuleData } from 'hooks/useApplyLiveRuleData';
import { useClaimRuleValidation } from 'hooks/useClaimRuleValidation';
import { ClaimSourcePreview } from './ClaimSourcePreview';
import { ClaimAttachmentsCard } from '../ClaimAttachmentsCard';
import { useValidationSummaryFeedback } from 'hooks/useValidationSummaryFeedback';

interface ClaimEditModalProps {
  table: MRT_TableInstance<Claim>;
  row: MRT_Row<Claim>;
  internalEditComponents?: React.ReactNode[];
  dirtyFields: MutableRefObject<{ [key: string]: boolean }>;
  stagedTariffActionsRef?: MutableRefObject<{
    claimID: string | null;
    hasStagedActions: boolean;
  }>;
  providers: Provider[];
  payors: Payor[];
  confidenceMetadata?: ClaimConfidenceInfo | null;
  headerActionsTarget?: string;
  deferSave?: boolean;
  onDeferredSave?: (args: {
    claimId: string;
    values: Claim;
    dirtyFields: Record<string, boolean>;
  }) => Promise<void> | void;
  onDeferredNext?: () => void;
  onDeferredFinish?: () => void;
  onDeferredCancel?: () => void;
  liveRules?: LiveClaimRulesState;
}

export const ClaimEditModal: React.FC<ClaimEditModalProps> = React.memo(
  ({
    table,
    row,
    internalEditComponents,
    dirtyFields,
    stagedTariffActionsRef,
    providers,
    payors,
    confidenceMetadata,
    headerActionsTarget,
    deferSave = false,
    onDeferredSave,
    onDeferredCancel,
    liveRules,
  }) => {
    const navigate = useNavigate();
    const computedColorScheme = useComputedColorScheme('dark');
    const isNarrowHeader = useMediaQuery('(max-width: 640px)');
    const theme = useMantineTheme();
    const { translate } = useTranslation();
    const { canModify } = useCanModify('claims');
    const role = getCognitoRole();
    const canViewInternalComments = role === 'USER' || role === 'ADMIN';
    const createComment = useCreateClaimComment(row.original.id);
    const patientHistoryContentID = useId();
    const [showPatientHistory, setShowPatientHistory] = useState(true);
    const [patientHistoryTotal, setPatientHistoryTotal] = useState<
      number | null
    >(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [showErrors, setShowErrors] = useState(false);
    const [hasEOBs, setHasEOBs] = useState<boolean>(false);
    const [showResubmitSection, setShowResubmitSection] =
      useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>('info');
    const [commentsExpanded, setCommentsExpanded] = useState(true);
    const [providerContactNote, setProviderContactNote] = useState<
      string | null
    >(row.original.provider?.contactNote || null);
    const [providerHasGroups, setProviderHasGroups] = useState<boolean>(
      !!row.original.provider?.groups?.length
    );
    const { form } = useActiveForm();
    const isSKClaim = useIsSKClaim(form);

    const { timezone } = useCurrentSiteStore();
    const [errors, setErrors] = useState<FormErrors>(form.errors);
    const {
      animateValidationFeedback,
      clearValidationFeedback,
      revealValidationSummary,
      showValidationFeedback,
      validationSummaryRef,
    } = useValidationSummaryFeedback();
    const [serviceStart, setServiceStart] = useState(
      () => form.getValues().serviceStart
    );
    const [isIncomplete, setIsIncomplete] = useState(() =>
      hasOnlyIncompleteTariffs(form.getValues())
    );
    form.watch('tariffs', ({ value }) => {
      setIsIncomplete(hasOnlyIncompleteTariffs({ tariffs: value }));
    });
    const index = row.id;
    const hasOCRSource = Boolean(confidenceMetadata?.documentID);
    const initialScrollTargetRef = useRef<HTMLDivElement>(null);
    const commentsSectionRef = useRef<HTMLDivElement>(null);
    const claimCommentsQuery = useClaimComments(
      row.original.id,
      canViewInternalComments
    );
    const commentCount = claimCommentsQuery.data?.length;
    const headerActionStyle = headerActionsTarget
      ? {
          gridColumn: isNarrowHeader ? '1 / -1' : 3,
          gridRow: isNarrowHeader ? 2 : 1,
          justifySelf: 'end',
          minWidth: 0,
        }
      : undefined;
    const getCommentShortcutLabel = (): string => {
      if (commentCount === undefined) {
        return translate(
          'pages:claims.comments.internalTitle',
          'Internal Comments'
        );
      }

      if (commentCount === 0) {
        return translate(
          'pages:claims.comments.shortcut',
          { count: 0 },
          'No internal comments yet'
        );
      }

      const fallback =
        commentCount === 1
          ? 'View 1 internal comment'
          : `View ${commentCount} internal comments`;
      return translate(
        'pages:claims.comments.shortcut',
        { count: commentCount },
        fallback
      );
    };

    useEffect(() => {
      const frameID = window.requestAnimationFrame(() => {
        initialScrollTargetRef.current?.scrollIntoView({
          behavior: 'auto',
          block: 'start',
        });
      });

      return () => window.cancelAnimationFrame(frameID);
    }, [hasOCRSource, row.original.id]);

    const relations = [
      'patient',
      'provider',
      'tariffs',
      'status',
      'site',
      'healthcard',
    ];
    const { clearEditDraft, getEditDraftById, saveEditDraft } =
      useClaimDraftStore();
    const { editPageLayout } = useSettingsStore();
    const validationAnimationName = 'claim-validation-shake';
    const pendingTariffResubmissions =
      useIsMutating({ mutationKey: ['resubmitClaimTariff'] }) > 0;
    const pendingClaimUpdates =
      useIsMutating({ mutationKey: ['updateClaim'] }) > 0;
    const pendingTariffWorkflow =
      pendingTariffResubmissions || pendingClaimUpdates;
    const hasFormErrors = useMemo(
      () => Object.keys(form.errors).length > 0,
      [form.errors]
    );
    const {
      clearServerRuleValidation,
      hasCurrentBlockingRules,
      liveBlockingRuleMessages,
      liveRuleError,
      liveRuleWarningMessages,
      isEvaluating,
      serverRuleErrors,
      setServerRuleFailure,
    } = useClaimRuleValidation(form, liveRules, isIncomplete);
    useApplyLiveRuleData(form, liveRules);

    form.watch('payorID', ({ previousValue, value }) => {
      // if the payorID changes, we need to update the payor);
      if (value && previousValue !== value) {
        const payor = payors.find((payor) => payor.id === value);
        if (payor) {
          form.setFieldValue('payor', payor);
        }
      }
    });

    form.watch('provider', ({ value }) => {
      // If the provider changes, we need to update the provider contact note
      const provider = value as Provider | null;
      setProviderContactNote(provider?.contactNote || null);
      setProviderHasGroups(!!provider?.groups?.length);
    });

    form.watch('serviceStart', ({ value }) => {
      setServiceStart(value as Claim['serviceStart']);
    });

    useEffect(() => {
      setErrors(form.errors);
      if (!hasFormErrors) {
        clearValidationFeedback();
      }
    }, [clearValidationFeedback, form.errors, hasFormErrors]);

    useEffect(() => {
      if (hasCurrentBlockingRules) {
        setShowErrors(true);
        revealValidationSummary();
      }
    }, [hasCurrentBlockingRules, revealValidationSummary]);

    useEffect(() => {
      row.original?.debits?.forEach((debit) => {
        if (debit?.eobs?.length > 0) {
          setHasEOBs(true);
        }
      });
      if (
        claimHasAnyTariffStatus(row.original, [
          CLAIM_TARIFF_STATUS.REJECTED,
          ...UNRECONCILED_CLAIM_TARIFF_STATUSES,
        ])
      ) {
        setShowResubmitSection(true);
      }
    }, []);

    const handleResubmission = async () => {
      const currentTariffs = (form.getValues().tariffs || []).map((tariff) => ({
        ...tariff,
        status: CLAIM_TARIFF_STATUS.READY,
      }));
      form.setFieldValue('tariffs', currentTariffs);
      await handleOnClick();
    };

    // validate on mount
    useEffect(() => {
      const valid = form.validate();
      if (valid.hasErrors) {
        setShowErrors(true);
        form.setErrors(valid.errors);
        setErrors(valid.errors);
      }
    }, []);

    const editComponentsMap = useMemo(() => {
      const map: Record<string, ReactNode> = {};
      internalEditComponents.forEach((comp: ReactElement) => {
        if (comp.key) map[comp.key as string] = comp;
      });
      return map;
    }, [internalEditComponents]);

    const handleOnClick = async () => {
      setIsLoading(true);
      clearServerRuleValidation();
      try {
        const formValues = form.getValues();
        const isIncompleteClaim = hasOnlyIncompleteTariffs(formValues);
        if (!isIncompleteClaim && (isEvaluating || hasCurrentBlockingRules)) {
          setShowErrors(true);
          revealValidationSummary();
          setIsLoading(false);
          return;
        }
        const updated = dirtyFields.current || {};
        const currentDirtyFields = form.getDirty();
        relations.forEach((relation) => {
          const anyNestedDirty = Object.keys(currentDirtyFields).some(
            (path) => {
              return (
                (path === relation && currentDirtyFields[path]) ||
                path.startsWith(relation + '.')
              );
            }
          );
          if (anyNestedDirty) {
            updated[relation] = true;
          }
        });
        updated.internalComments =
          canViewInternalComments &&
          Boolean(
            currentDirtyFields.internalComments &&
              form.getValues().internalComments?.trim()
          );
        dirtyFields.current = updated;
        const isValidForSave = isIncompleteClaim
          ? claimEditSaveSchema.safeParse(formValues).success
          : form.isValid();
        if (!isValidForSave) {
          const validation = form.validate();
          setShowErrors(true);
          setErrors(validation.errors);
          revealValidationSummary();
          setIsLoading(false);
          return;
        }
        row._valuesCache = formValues as any;
        clearEditDraft(row.original.id);
        if (deferSave && onDeferredSave) {
          await onDeferredSave({
            claimId: row.original.id,
            values: form?.getValues() as Claim,
            dirtyFields: { ...dirtyFields.current },
          });
          setIsSaved(true);
          return;
        }
        await table.options.onEditingRowSave?.({
          exitEditingMode: () => {
            table.setEditingRow(null);
          },
          row,
          table,
          values: form?.getValues() as any,
        });
        // After successful save, return to list route (removes /edit/:id param)
        navigate('/claims');
      } catch (e) {
        if (setServerRuleFailure(e)) {
          setShowErrors(true);
          revealValidationSummary();
        }
        Sentry.captureException(e);
      } finally {
        setIsLoading(false);
      }
    };

    const handleSaveInternalComments = async () => {
      if (!canViewInternalComments || !canModify || pendingTariffWorkflow)
        return;
      const text = form.getValues().internalComments?.trim();
      if (!text) return;

      try {
        createComment.reset();
        await createComment.mutateAsync(text);
        form.setFieldValue('internalComments', '');
        if (getEditDraftById(row.original.id)) {
          saveEditDraft(row.original.id, form);
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    const handleCancel = () => {
      const hasStagedTariffActions = stagedTariffActionsRef
        ? stagedTariffActionsRef.current.hasStagedActions &&
          stagedTariffActionsRef.current.claimID === row.original.id
        : dirtyFields.current?.__stagedTariffActions;
      if (
        hasStagedTariffActions &&
        !window.confirm(
          'You have staged tariff actions that have not been saved. Close without saving?'
        )
      ) {
        return;
      }

      if (stagedTariffActionsRef) {
        stagedTariffActionsRef.current = {
          claimID: null,
          hasStagedActions: false,
        };
      }
      table.setEditingRow(null);
      if (deferSave) {
        onDeferredCancel?.();
        return;
      }
      navigate('/claims');
    };

    const infoSectionKeys: AllKeys[] = useMemo(() => editInfoSectionKeys, []);

    const eobSectionKeys: AllKeys[] = useMemo(() => eobKeys, []);

    const detailSectionKeys: AllKeys[] = useMemo(
      () => [
        ...editDetailSectionKeys.filter(
          (key) => !claimCommentSectionKeys.includes(key)
        ),
        ...(isSKClaim ? SKClaimDetailSectionKeys : []),
      ],
      [isSKClaim]
    );

    const commentsHasError = Object.keys(errors).some((key) =>
      claimCommentSectionKeys.includes(key as AllKeys)
    );

    const commentSection = (
      <ClaimCommentSection
        index={index}
        title={translate(
          'pages:claims.comments.internalTitle',
          'Internal Comments'
        )}
        keys={claimCommentSectionKeys}
        cols={1}
        editComponentsMap={editComponentsMap}
        claimID={row.original.id}
        commentsQuery={claimCommentsQuery}
        timezone={row.original.site?.timezone ?? timezone}
        onSaveComment={deferSave ? undefined : handleSaveInternalComments}
        canDeleteComment={canModify && !pendingTariffWorkflow}
        canSaveComment={
          canModify &&
          !pendingTariffWorkflow &&
          form.isDirty('internalComments') &&
          Boolean(form.getValues().internalComments?.trim())
        }
        isSavingComment={createComment.isPending}
        isCommentSaveError={createComment.isError}
        collapsible
        expanded={commentsExpanded}
        onExpandedChange={setCommentsExpanded}
        compact
      />
    );

    const patientSectionKeys: AllKeys[] = useMemo(() => patientKeys, []);

    const healthcardSectionKeys: AllKeys[] = useMemo(() => healthcardKeys, []);

    const providerSectionKeys: AllKeys[] = useMemo(() => providerKeys, []);

    const tariffSectionKeys: AllKeys[] = useMemo(() => tariffKeys, []);

    const infoHasError = Object.keys(errors).some((key) =>
      infoSectionKeys.includes(key as AllKeys)
    );
    const eobHasError = Object.keys(errors).some((key) =>
      eobSectionKeys.includes(key as AllKeys)
    );
    const detailHasError = Object.keys(errors).some((key) =>
      detailSectionKeys.includes(key as AllKeys)
    );
    const patientHasError = Object.keys(errors).some((key) =>
      patientSectionKeys.includes(key as AllKeys)
    );
    // For provider, include an extra check for "providerID"
    const providerHasError = Object.keys(errors).some(
      (key) =>
        providerSectionKeys.includes(key as AllKeys) || key === 'providerID'
    );

    const tariffHasError = Object.keys(errors).some((key) =>
      key.startsWith('tariffs.')
    );

    const determineSubmitDisabledReason = (row: MRT_Row<Claim>): string => {
      const fallback = '';
      const formValues = form.getValues();
      const resolvedPayor = payors.find(
        (payor) => payor.id === (formValues.payorID || row.original.payorID)
      );
      const payorType =
        resolvedPayor?.type ??
        row.original.payor?.type ??
        formValues.payor?.type;
      if (pendingTariffWorkflow) {
        return translate('pages:claims.tariffResubmissionInProgress');
      }
      if (!canModify) {
        return translate('actionDisabledReason.editingDisabledNoPermission');
      }
      if (
        claimHasAnyTariffStatus(row.original, [CLAIM_TARIFF_STATUS.PENDING])
      ) {
        return translate('pages:claims.pendingActionDisabled');
      }
      const hasSubmittedTariff = claimHasAnyTariffStatus(row.original, [
        CLAIM_TARIFF_STATUS.SUBMITTED,
      ]);
      const hasQueriedTariff = claimHasAnyTariffStatus(row.original, [
        CLAIM_TARIFF_STATUS.QUERIED,
      ]);
      if (hasSubmittedTariff && !isSubmittedClaimPayorEditable(payorType)) {
        return translate('actionDisabledReason.editingDisabledClaimSubmitted');
      }
      if (hasQueriedTariff) {
        return translate('actionDisabledReason.editingDisabledBeingQueried');
      }
      return fallback;
    };
    const hasFutureServiceStart = isFutureDate(serviceStart, timezone);
    const editDisabledReason = determineSubmitDisabledReason(row);
    const submitDisabledReason = isEvaluating
      ? translate(
          'pages:claims.modals.rulesChecking',
          'Checking billing rules…'
        )
      : hasCurrentBlockingRules
      ? translate(
          'pages:claims.modals.rulesBlockingSave',
          'Resolve the billing rule errors before saving.'
        )
      : hasFutureServiceStart
      ? 'A claim with a future service date cannot be submitted.'
      : editDisabledReason;
    const draftMenuDisabled =
      isEvaluating || hasFutureServiceStart || Boolean(editDisabledReason);
    const validationAnimationStyles = useMemo(
      () =>
        showValidationFeedback
          ? {
              animation: animateValidationFeedback
                ? `${validationAnimationName} 180ms ease`
                : undefined,
            }
          : undefined,
      [
        animateValidationFeedback,
        showValidationFeedback,
        validationAnimationName,
      ]
    );

    return (
      <div
        ref={hasOCRSource ? undefined : initialScrollTargetRef}
        data-testid="claim-edit-modal-top"
        style={{ position: 'relative', scrollMarginTop: 16 }}
      >
        <style>{getButtonShakeAnimation(validationAnimationName)}</style>
        <LoadingOverlay visible={isLoading} />
        <Card shadow="lg" radius="sm" padding="lg" withBorder>
          <div
            ref={validationSummaryRef}
            data-testid="claim-validation-summary"
            tabIndex={-1}
            style={{ scrollMarginTop: 'var(--mantine-spacing-md)' }}
          />
          <OptionalPortal
            withinPortal={Boolean(headerActionsTarget)}
            target={headerActionsTarget}
          >
            <Flex
              justify="flex-end"
              align="center"
              gap="sm"
              mb={headerActionsTarget ? 0 : 'md'}
              style={headerActionStyle}
            >
              {canViewInternalComments && (
                <Indicator
                  color="red"
                  disabled={!commentsHasError}
                  size={10}
                  withBorder
                >
                  <Button
                    variant="outline"
                    color="gray"
                    c={isLoading ? 'dimmed' : 'var(--mantine-color-text)'}
                    size="xs"
                    disabled={isLoading}
                    onClick={() => {
                      setCommentsExpanded(true);
                      commentsSectionRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                    }}
                  >
                    {getCommentShortcutLabel()}
                  </Button>
                </Indicator>
              )}
              <ActionIcon
                aria-label={translate('buttons.close', 'Close')}
                variant="subtle"
                color="gray"
                radius="xl"
                onClick={handleCancel}
                disabled={isLoading}
              >
                <IconX size={18} />
              </ActionIcon>
            </Flex>
          </OptionalPortal>
          <FutureServiceDateAlert
            serviceStarts={[serviceStart]}
            timezone={timezone}
          />
          {serverRuleErrors.length > 0 && (
            <Flex justify="center" align="stretch" gap="md" mb="md">
              <Alert
                title={translate(
                  'pages:claims.modals.ruleValidationFailedTitle',
                  'Rule validation failed'
                )}
                color="red"
                radius="sm"
              >
                {serverRuleErrors.map((message) => (
                  <Text key={message}>{message}</Text>
                ))}
              </Alert>
            </Flex>
          )}
          {liveBlockingRuleMessages.length > 0 &&
            serverRuleErrors.length === 0 && (
              <Flex justify="center" align="stretch" gap="md" mb="md">
                <Alert
                  title={translate(
                    'pages:claims.modals.ruleValidationFailedTitle',
                    'Rule validation failed'
                  )}
                  color="red"
                  radius="sm"
                >
                  {liveBlockingRuleMessages.map((message) => (
                    <Text key={message}>{message}</Text>
                  ))}
                </Alert>
              </Flex>
            )}
          {liveRuleWarningMessages.length > 0 && (
            <Flex justify="center" align="stretch" gap="md" mb="md">
              <Alert
                title={translate(
                  'pages:claims.modals.ruleWarningsTitle',
                  'Billing rule warnings'
                )}
                color="yellow"
                radius="sm"
              >
                {liveRuleWarningMessages.map((message) => (
                  <Text key={message}>{message}</Text>
                ))}
              </Alert>
            </Flex>
          )}
          <Text
            size="sm"
            c="dimmed"
            ta="center"
            mb="md"
            aria-hidden={!isEvaluating}
          >
            {isEvaluating
              ? translate(
                  'pages:claims.modals.rulesChecking',
                  'Checking billing rules…'
                )
              : '\u00a0'}
          </Text>
          {liveRuleError && !isEvaluating && (
            <Alert
              title={translate(
                'pages:claims.modals.rulesUnavailableTitle',
                'Rule preview unavailable'
              )}
              color="yellow"
              radius="sm"
              mb="md"
            >
              {translate(
                'pages:claims.modals.rulesUnavailableMessage',
                'Rules will be checked again when you save.'
              )}
            </Alert>
          )}
          {confidenceMetadata?.documentID && (
            <div
              ref={initialScrollTargetRef}
              data-testid="claim-edit-modal-ocr"
              style={{ scrollMarginTop: 16 }}
            >
              <ClaimSourcePreview
                claimID={row.original.id}
                confidenceMetadata={confidenceMetadata}
              />
            </div>
          )}
          {showErrors && Object.keys(form.errors).length > 0 && (
            <Flex justify="center" align="stretch" gap="md">
              <Alert title="Form is invalid" color="red" radius="sm">
                {Object.keys(form.errors)
                  .filter(Boolean)
                  .map((key) => (
                    <React.Fragment key={key}>
                      {form.errors[key] && (
                        <Text>
                          {key}: {form.errors[key]}
                        </Text>
                      )}
                    </React.Fragment>
                  ))}
              </Alert>
            </Flex>
          )}
          {providerContactNote && (
            <Flex justify="center" align="stretch" gap="md" mt="md" mb="md">
              <Alert title="Provider Contact Note" color="blue" radius="sm">
                {providerContactNote}
              </Alert>
            </Flex>
          )}
          {editPageLayout === 'tabbed' ? (
            <Tabs
              value={activeTab}
              onChange={setActiveTab}
              variant="pills"
              keepMounted
            >
              <Tabs.List justify="center">
                <Tabs.Tab value="info">
                  <Indicator
                    color="red"
                    disabled={!infoHasError}
                    size={10}
                    withBorder
                  >
                    Claim Info
                  </Indicator>
                </Tabs.Tab>
                {hasEOBs && (
                  <Tabs.Tab value="eob">
                    <Indicator
                      color="red"
                      disabled={!eobHasError}
                      size={10}
                      withBorder
                    >
                      EOBs
                    </Indicator>
                  </Tabs.Tab>
                )}
                <Tabs.Tab value="detail">
                  <Indicator
                    color="red"
                    disabled={!detailHasError}
                    size={10}
                    withBorder
                  >
                    Claim Details
                  </Indicator>
                </Tabs.Tab>
                <Tabs.Tab value="patient">
                  <Indicator
                    color="red"
                    disabled={!patientHasError}
                    size={10}
                    withBorder
                  >
                    Patient
                  </Indicator>
                </Tabs.Tab>
                <Tabs.Tab value="provider">
                  <Indicator
                    color="red"
                    disabled={!providerHasError}
                    size={10}
                    withBorder
                  >
                    Provider
                  </Indicator>
                </Tabs.Tab>
                <Tabs.Tab value="tariff">
                  <Indicator
                    color="red"
                    disabled={!tariffHasError}
                    size={10}
                    withBorder
                  >
                    Tariffs
                  </Indicator>
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="info" pt="md">
                <ClaimInfoSection
                  index={index}
                  title="Claim Info"
                  keys={infoSectionKeys}
                  cols={4}
                  editComponentsMap={editComponentsMap}
                />
              </Tabs.Panel>

              <Tabs.Panel value="eob" pt="md">
                <ClaimEOBSection
                  index={index}
                  title="EOBs"
                  keys={eobSectionKeys}
                  cols={1}
                  editComponentsMap={editComponentsMap}
                />
              </Tabs.Panel>

              <Tabs.Panel value="detail" pt="md">
                <ClaimDetailSection
                  index={index}
                  title="Claim Details"
                  keys={detailSectionKeys}
                  cols={4}
                  editComponentsMap={editComponentsMap}
                />
              </Tabs.Panel>
              <Tabs.Panel value="patient" pt="md">
                <ClaimPatientSection
                  index={index}
                  title="Patient"
                  keys={patientSectionKeys}
                  cols={3}
                  editComponentsMap={editComponentsMap}
                  isNewPatient={false}
                />
                <ClaimHealthcardSection
                  index={index}
                  title="Healthcard"
                  keys={healthcardSectionKeys}
                  cols={3}
                  editComponentsMap={editComponentsMap}
                />
              </Tabs.Panel>

              <Tabs.Panel value="provider" pt="md">
                <ClaimProviderSection
                  index={index}
                  providers={providers}
                  title="Provider"
                  keys={providerSectionKeys}
                  cols={providerHasGroups ? 3 : 2}
                  editComponentsMap={editComponentsMap}
                />
              </Tabs.Panel>

              <Tabs.Panel value="tariff" pt="md">
                <ClaimTariffSection
                  index={index}
                  title="Tariffs"
                  keys={tariffSectionKeys}
                  cols={1}
                  editComponentsMap={editComponentsMap}
                />
              </Tabs.Panel>
            </Tabs>
          ) : (
            <Flex direction="column" gap="xl" mt="md">
              <ClaimInfoSection
                index={index}
                title="Claim Info"
                keys={infoSectionKeys}
                cols={4}
                editComponentsMap={editComponentsMap}
              />
              {hasEOBs && (
                <ClaimEOBSection
                  index={index}
                  title="EOBs"
                  keys={eobSectionKeys}
                  cols={1}
                  editComponentsMap={editComponentsMap}
                />
              )}
              <ClaimDetailSection
                index={index}
                title="Claim Details"
                keys={detailSectionKeys}
                cols={4}
                editComponentsMap={editComponentsMap}
              />
              <Flex direction="column" gap="md">
                <ClaimPatientSection
                  index={index}
                  title="Patient"
                  keys={patientSectionKeys}
                  cols={3}
                  editComponentsMap={editComponentsMap}
                  isNewPatient={false}
                />
                <ClaimHealthcardSection
                  index={index}
                  title="Healthcard"
                  keys={healthcardSectionKeys}
                  cols={3}
                  editComponentsMap={editComponentsMap}
                />
              </Flex>
              <ClaimProviderSection
                index={index}
                providers={providers}
                title="Provider"
                keys={providerSectionKeys}
                cols={2}
                editComponentsMap={editComponentsMap}
              />
              <ClaimTariffSection
                index={index}
                title="Tariffs"
                keys={tariffSectionKeys}
                cols={1}
                editComponentsMap={editComponentsMap}
              />
            </Flex>
          )}
          <Flex justify={'center'} gap={'lg'} style={{ marginTop: '1%' }}>
            <Button
              variant="outline"
              radius="md"
              color="gray"
              c={buttonTextColor(computedColorScheme, false)}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            {deferSave ? (
              <Button
                type="submit"
                variant={'outline'}
                color={showValidationFeedback ? 'red' : 'green'}
                c={
                  showValidationFeedback
                    ? theme.white
                    : buttonTextColor(computedColorScheme, isLoading)
                }
                onClick={handleOnClick}
                loading={isLoading}
                disabled={
                  pendingTariffWorkflow ||
                  hasFutureServiceStart ||
                  isEvaluating ||
                  hasCurrentBlockingRules
                }
                leftSection={
                  isSaved ? <IconCircleCheck /> : <IconDeviceFloppy />
                }
                data-testid="edit-claim-save"
                style={validationAnimationStyles || {}}
              >
                {isSaved ? 'Saved' : 'Save'}
              </Button>
            ) : (
              <ClaimModalSubmitButton
                table={table}
                computedColorScheme={computedColorScheme}
                isLoading={isLoading}
                handleOnClick={handleOnClick}
                showResubmitSection={showResubmitSection}
                handleResubmission={handleResubmission}
                disabled={!!submitDisabledReason}
                menuDisabled={draftMenuDisabled}
                disabledReason={submitDisabledReason}
                showValidationFeedback={showValidationFeedback}
                validationAnimationActive={animateValidationFeedback}
              />
            )}
          </Flex>
        </Card>
        <ClaimAttachmentsCard
          claimID={row.original.id}
          canUpload={canModify}
          timezone={row.original.site?.timezone}
        />
        {canViewInternalComments && (
          <div
            ref={commentsSectionRef}
            style={{
              scrollMarginTop: 16,
              marginTop: 'var(--mantine-spacing-md)',
            }}
          >
            {commentSection}
          </div>
        )}
        <RelatedClaimsPanel claim={row.original} />
        <Card shadow="lg" radius="sm" padding="lg" withBorder mt="md">
          <UnstyledButton
            w="100%"
            onClick={() => setShowPatientHistory((prev) => !prev)}
            aria-expanded={showPatientHistory}
            aria-controls={patientHistoryContentID}
          >
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <IconHistory size={20} />
                <Text fw={700}>
                  {translate(
                    'pages:claims.patientHistory.title',
                    'Patient Claim History'
                  )}
                </Text>
                {patientHistoryTotal !== null && (
                  <Badge
                    variant="light"
                    color="gray"
                    size="sm"
                    data-testid="patient-claim-history-count"
                  >
                    {patientHistoryTotal}
                  </Badge>
                )}
              </Group>
              <ActionIcon
                component="span"
                size="input-sm"
                variant="subtle"
                color="gray"
                radius="sm"
                aria-hidden
              >
                <IconChevronDown
                  size={18}
                  style={{
                    transform: showPatientHistory
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 150ms ease',
                  }}
                />
              </ActionIcon>
            </Group>
          </UnstyledButton>
          <Collapse id={patientHistoryContentID} in={showPatientHistory}>
            <Box mt="md">
              <PatientHistoryModal
                variant="inline"
                initialPatientID={row.original.patientID}
                initialPatient={row.original.patient}
                initialHealthcard={row.original.healthcard}
                payors={payors}
                onClaimHistoryTotalChange={setPatientHistoryTotal}
              />
            </Box>
          </Collapse>
        </Card>
      </div>
    );
  }
);
